import React, { FC, MutableRefObject, ReactElement, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FlexboxVertical } from './Layout';

export interface AnimatedContainerProps {
  animationActive?: boolean;
  animationDelay?: number;
}

export const AnimatedContainer = styled(FlexboxVertical)<{ active?: boolean, delay?: number }>`
  animation: ${({ active = false }) => active ? 'appear' : 'disappear' } 300ms running;
  animation-delay: ${({ delay = 0 }) => delay }ms;
  animation-fill-mode: both;
  animation-timing-function: ease-in-out;
  
  opacity: ${({ active = false }) => active ? 0 : 1 };
  
  @keyframes appear {
    to {
      opacity: 1;
    }
  }
  
  @keyframes disappear {
    to {
      opacity: 0;
    }
  }
`;

export const AnimatedContainerBounce = styled(FlexboxVertical)<{ active?: boolean, delay?: number }>`
  animation: ${({ active = false }) => active ? 'bounce' : 'unbounce' } 600ms running;
  animation-delay: ${({ delay = 0 }) => delay }ms;
  animation-fill-mode: both;
  animation-timing-function: ease-in-out;
  
  @keyframes bounce {
    0%   { transform: scale(1,1) }
    10%  { transform: scale(1.1,.9) }
    30%  { transform: scale(.9,1.1) }
    50%  { transform: scale(1.05,.95) }
    57%  { transform: scale(1,1) }
    64%  { transform: scale(1,1) }
    100% { transform: scale(1,1) }
  }
  
  @keyframes unbounce {
    //0%   { transform: scale(1,1) }
    //10%  { transform: scale(1.1,.9) }
    //30%  { transform: scale(.9,1.1) }
    //50%  { transform: scale(1.05,.95) }
    //57%  { transform: scale(1,1) }
    //64%  { transform: scale(1,1) }
    //100% { transform: scale(1,1) }
  }
`;

export const calculateAnimationDelayItemsAppear = (visible: boolean, index: number, itemCount: number) => (
  visible
    ? Math.min(500, (100 * (1 - Math.pow(1 - (index+1)/itemCount, 3) ))*(index+1))     // easeOutCubic (0 - 10) * 100ms or 500ms
    : 50
)

interface AnimatedWhenVisibleProps {
  viewRef: MutableRefObject<HTMLDivElement | null>;
  children: ReactElement | ((readyToAnimate: boolean) => ReactElement);
  viewHeight?: number;
  visible?: boolean;
}
export const AnimatedWhenVisible: FC<AnimatedWhenVisibleProps> = ({ viewRef, visible = true, children, ...props }) => {
  const [animateView, setAnimateView] = useState(false);
  const visibleRef = useRef<boolean>(visible);

  const updateView = () => {
    if (!animateView && visibleRef.current) {
      const scrollView = document.querySelector('main.app') as HTMLDivElement;
      const pageBottom = scrollView?.scrollTop + window.innerHeight;
      const chartHeight = props.viewHeight || viewRef.current?.offsetHeight;
      let offsetParent = viewRef.current;
      let chartBottom = 0;
      do {
        if (offsetParent) {
          chartBottom += offsetParent.offsetTop;
        }
        offsetParent = offsetParent?.offsetParent as HTMLDivElement;
      } while (offsetParent && offsetParent !== scrollView);

      if (pageBottom > chartBottom + (2*chartHeight / 3)) {
        setAnimateView(true);
      }
    }
  }

  useEffect(() => {
    const listener = (event: any) => {
      updateView();
    };

    setAnimateView(false);

    const scrollView = document.querySelector('main.app') as HTMLDivElement;
    if (scrollView) {
      scrollView.addEventListener('scroll', listener);
    }
    return () => {
      if (scrollView) {
        scrollView.removeEventListener('scroll', listener);
      }
    }
  }, []);

  useEffect(() => {
    visibleRef.current = visible;
    updateView();
  }, [visible]);

  return <>{ typeof children === 'function' ? children(animateView) : children }</>;
}

interface AnimatedContainerScrollProps {
  viewRef: MutableRefObject<HTMLDivElement | null>;
  viewHeight?: number;
  children: ReactElement | ((animationDelta: number) => ReactElement);
}
export const AnimatedContainerScroll: FC<AnimatedContainerScrollProps> = ({ viewRef, children, ...props }) => {
  const [animationDelta, setAnimationDelta] = useState<number>(0);

  const updateView = () => {
    const scrollView = document.querySelector('main.app') as HTMLDivElement;
    const pageTop = scrollView?.scrollTop || 0;
    const pageBottom = pageTop + window.innerHeight;
    const viewHeight = props.viewHeight || viewRef.current?.offsetHeight || 0;
    let offsetParent = viewRef.current;
    let viewPos = 0;
    do {
      if (offsetParent) {
        viewPos += offsetParent.offsetTop;
      }
      offsetParent = offsetParent?.offsetParent as HTMLDivElement;
    } while (offsetParent && offsetParent !== scrollView);

    const delta = (window.innerHeight - (viewPos-pageTop)) / window.innerHeight;
    setAnimationDelta(Math.max(0, Math.min(1, delta * 1.5)));
  }

  useEffect(() => {
    const listener = (event: any) => {
      updateView();
    };
    updateView();

    const scrollView = document.querySelector('main.app') as HTMLDivElement;
    if (scrollView) {
      scrollView.addEventListener('scroll', listener);
    }
    return () => {
      if (scrollView) {
        scrollView.removeEventListener('scroll', listener);
      }
    }
  }, []);

  return <>{ typeof children === 'function' ? children(animationDelta) : children }</>;
}
